import { createContext, useContext, useMemo, type ReactNode } from 'react'

import { isAuthenticated } from '@/features/auth/api'
import { useMe } from '@/features/auth/hooks'
import type { MeResponse, Role } from '@/features/auth/types'

interface AuthContextValue {
  isAuthenticated: boolean
  isLoading: boolean
  isError: boolean
  me: MeResponse | undefined
  user: MeResponse['user'] | undefined
  role: Role | undefined
  redirectPath: string
  dashboard: MeResponse['redirect']['dashboard'] | undefined
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { data: me, isLoading, isError } = useMe()

  const value = useMemo<AuthContextValue>(() => {
    const hasToken = isAuthenticated()
    const authenticated = Boolean(me)

    return {
      isAuthenticated: authenticated,
      isLoading: isLoading || (hasToken && !me && !isError),
      isError,
      me,
      user: me?.user,
      role: me?.role,
      redirectPath: me?.redirect.path ?? '/',
      dashboard: me?.redirect.dashboard,
    }
  }, [me, isLoading, isError])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
